import { useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import Add from '@factorialco/f0-react/icons/app/Add'
import {
  TrainingBudgetsDocument,
  TrainingClassDetailIndexPageDocument,
  UpdateCostsClassDetailDocument,
  UpdateCostsClassDetailMutationVariables,
} from 'generated/resources/graphql'

import MoneyField from 'modules/core/components/MoneyField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useDebounced } from 'modules/trainings/components/Revamp/TrainingsDetail/Fundae/finalizar-grupo-formativo/content/utils'
import { SalaryCostCalculator } from 'modules/trainings/components/SalaryCostCalculator'

interface ClassCostsValues {
  cost: string
  subsidizedCost?: string
  indirectCost?: string
  salaryCost?: string
}

const CostsTab = () => {
  const { classId } = useParams()
  const { company } = useCurrentSession()

  // companyId is 0 because we are not using the companyId to get the feature flag
  const { data } = useQuery(TrainingClassDetailIndexPageDocument, {
    variables: { companyId: company.id, trainingClassId: Number(classId), withFF: true },
  })
  const currentClass = data?.trainings.trainingClass ?? undefined

  const { has_dev_training_budgets } = checkFeatures(
    ['dev_training_budgets'],
    data?.features?.featuresConnection.nodes
  )

  const { data: budgetsData } = useQuery(TrainingBudgetsDocument, {
    variables: {
      budgetableBy: ['training_class'],
    },
    skip: !has_dev_training_budgets,
  })

  const budgetOptions = (budgetsData?.finance.budgetsConnection.edges || []).map((edge) => ({
    value: edge.node.id,
    label: edge.node.name,
  }))

  const [updateCosts] = useMutation(UpdateCostsClassDetailDocument, {
    extractorFn(d) {
      return d.trainingsMutations.updateTrainingsTrainingClass
    },
  })
  const debouncedUpdateCosts = useDebounced(updateCosts)
  const { values, setValues, handleSubmit, errors, validateForm } = useForm<ClassCostsValues>({
    enableReinitialize: true,
    defaultValues: {
      cost: currentClass?.cost || '',
      subsidizedCost: currentClass?.subsidizedCost || '',
      indirectCost: currentClass?.indirectCost || '',
      salaryCost: currentClass?.salaryCost || '',
    },
    validate: (data) => {
      const errors: FormErrors<ClassCostsValues> = {}
      if (Number(data.cost) < 0) {
        errors.cost = i18n.t('trainings.classes.errors.greater_than_zero')
      }
      if (Number(data.cost) > 999999) {
        errors.cost = i18n.t('trainings.classes.errors.less_than')
      }
      if (Number(data.subsidizedCost) < 0) {
        errors.subsidizedCost = i18n.t('trainings.classes.errors.greater_than_zero')
      }
      if (Number(data.subsidizedCost) > 999999) {
        errors.subsidizedCost = i18n.t('trainings.classes.errors.less_than')
      }
      if (Number(data.indirectCost) < 0) {
        errors.indirectCost = i18n.t('trainings.classes.errors.greater_than_zero')
      }
      if (Number(data.indirectCost) > 999999) {
        errors.indirectCost = i18n.t('trainings.classes.errors.less_than')
      }
      if (Number(data.salaryCost) < 0) {
        errors.salaryCost = i18n.t('trainings.classes.errors.greater_than_zero')
      }
      if (Number(data.salaryCost) > 999999) {
        errors.salaryCost = i18n.t('trainings.classes.errors.less_than')
      }
      return errors
    },
    onSubmit: async (classData) => {
      const payload: UpdateCostsClassDetailMutationVariables = {
        id: Number(currentClass?.id),
        ...classData,
      }
      const result = await debouncedUpdateCosts({
        variables: {
          id: payload.id,
          cost: payload.cost && payload.cost.length > 0 ? payload.cost : '0',
          salaryCost:
            payload.salaryCost && payload.salaryCost?.length > 0 ? payload.salaryCost : '0',
          indirectCost:
            payload.indirectCost && payload.indirectCost.length > 0 ? payload.indirectCost : '0',
          subsidizedCost:
            payload.subsidizedCost && payload.subsidizedCost?.length > 0
              ? payload.subsidizedCost
              : '0',
        },
        refetchQueries: [TrainingClassDetailIndexPageDocument],
      })
      if (!!result) {
        return handleResult(result, () => {})
      }
      return Promise.reject()
    },
  })

  const handleFieldChange = async (field: keyof ClassCostsValues, newValue: string) => {
    const newValues = {
      ...values,
      [field]: newValue,
    }
    setValues(newValues)
    const validationErrors = await validateForm(newValues)

    if (
      !validationErrors.cost &&
      !validationErrors.subsidizedCost &&
      !validationErrors.indirectCost &&
      !validationErrors.salaryCost
    ) {
      handleSubmit()
    }
  }

  const currency = currentClass?.currency || company.currency || ''
  return (
    <Box width="full">
      <SectionHeader
        title={i18n.t('trainings.trainings_revamp.classes.cost_header.title')}
        description={i18n.t('trainings.trainings_revamp.classes.cost_header.description')}
        separator="bottom"
      />
      <Box paddingTop="s20" flexDirection="column" gap="s16">
        <Box gap="s8">
          <Text size="300" color="textPrimary" weight="semibold">
            {i18n.t('trainings.trainings_detail.fundae.costs.direct_costs.label')}
          </Text>
          <Text size="200">{i18n.t('trainings.classes.cost_desc')}</Text>
          <Box maxWidth="s400">
            <MoneyField
              currency={currency}
              mandatory
              onChange={(newValue) => handleFieldChange('cost', newValue)}
              error={errors.cost}
              value={values.cost}
            />
          </Box>
        </Box>

        <Box gap="s8">
          <Text size="300" color="textPrimary" weight="semibold">
            {i18n.t('trainings.trainings_detail.fundae.costs.indirect_costs.label')}
          </Text>
          <Text size="200">{i18n.t('trainings.classes.indirect_cost_desc')}</Text>
          <Box maxWidth="s400">
            <MoneyField
              currency={currency}
              mandatory
              error={errors.indirectCost}
              onChange={(newValue) => handleFieldChange('indirectCost', newValue)}
              value={values.indirectCost}
            />
          </Box>
        </Box>

        <Box gap="s8">
          <Text size="300" color="textPrimary" weight="semibold">
            {i18n.t('trainings.trainings_detail.fundae.costs.salary_costs.label')}
          </Text>
          <Text size="200">{i18n.t('trainings.classes.salary_cost_desc')}</Text>
          <Box gap="s16" flexDirection="row" alignItems="center">
            <Box maxWidth="s400">
              <MoneyField
                currency={currency}
                mandatory
                error={errors.salaryCost}
                onChange={(newValue) => handleFieldChange('salaryCost', newValue)}
                value={values.salaryCost}
              />
            </Box>
            <SalaryCostCalculator
              classId={Number(currentClass?.id)}
              onApply={(newValue) => {
                handleFieldChange('salaryCost', newValue)
                return true
              }}
              trackingSelectedFrom="groups_cost_tab"
              currency={currency}
            />
          </Box>
        </Box>

        {has_dev_training_budgets && (
          <Box gap="s8">
            <Text size="300" color="textPrimary" weight="semibold">
              {i18n.t('trainings.trainings_revamp.training_class.class_wizard.total_cost_group')}
            </Text>
            <Text size="200">
              {i18n.t(
                'trainings.trainings_revamp.training_class.class_wizard.total_cost_group_desc'
              )}
            </Text>
            <Box gap="s16" flexDirection="row" alignItems="center">
              <Box maxWidth="s400">
                <MoneyField currency={currency} readonly value={currentClass?.netCost || '0'} />
              </Box>
              <Box minWidth="s200" height="full">
                <F0Select
                  placeholder={i18n.t('trainings.classes.select_budget')}
                  showSearchBox
                  searchBoxPlaceholder={i18n.t('common.search')}
                  options={budgetOptions.map((b) => ({ label: b.label, value: String(b.value) }))}
                  onChange={() => {}}
                  size="md"
                  actions={[
                    {
                      label: i18n.t('trainings.classes.create_budget'),
                      onClick: () => {},
                      variant: 'ghost',
                      icon: Add,
                    },
                  ]}
                />
              </Box>
            </Box>
          </Box>
        )}

        <Box gap="s8">
          <Text size="300" color="textPrimary" weight="semibold">
            {i18n.t(
              'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.reviewed_subsidized_cost'
            )}
          </Text>
          <Text size="200">{i18n.t('trainings.classes.subsidized_cost_desc')}</Text>
          <Box maxWidth="s400">
            <MoneyField
              currency={currency}
              value={values.subsidizedCost}
              onChange={(value) => handleFieldChange('subsidizedCost', value)}
              readonly={false}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
export default CostsTab
